import React, { useEffect, useState } from 'react';
import { Btn, DangerBtn, Loading, Msg, Section, fmtDate, panelPost, useAdminGet } from '../kit';
import { Badge, Labeled, Modal, inputCls } from '../email/emailKit';

/**
 * Suspending an account — rare, for abuse (someone harvesting university
 * contacts, scraping, spam, fraud). Backend/account_status.py: users.is_active
 * is the one switch, and every sign-in route, signed-in call, search, contact
 * card and the WhatsApp bot now refuse a suspended account.
 *
 * GET users/account_status, POST users/disable / users/enable. Every change is
 * logged (account_status_log) and shown here as the account's history; the
 * reason is internal — the user is never shown it, even when e-mailed.
 */

type Flash = { kind: 'error' | 'ok'; text: string } | null;

const CATEGORY_LABEL: Record<string, string> = {
  contact_harvesting: 'Contact harvesting',
  scraping: 'Scraping',
  spam: 'Spam',
  fraud: 'Fraud',
  terms: 'Breach of terms',
  other: 'Other',
};
const CATEGORIES = Object.keys(CATEGORY_LABEL);
const catLabel = (c: any) => CATEGORY_LABEL[String(c || '')] || String(c || '') || 'no category';

/** Suspending changes what the bell shows (a contact-abuse flag, a mandate). */
const alertsChanged = () => {
  try { document.dispatchEvent(new Event('utech:alerts-changed')); } catch { /* not in a browser */ }
};

/** What the suspension did to billing, from the log row's `billing` JSON
 *  (billing_service.stop_billing_for_user's answer, or {skipped}). */
const billingText = (raw: any): { text: string; bad: boolean } => {
  let b = raw;
  if (typeof b === 'string') { try { b = JSON.parse(b); } catch { b = null; } }
  if (!b || b.skipped) return { text: 'renewals left alone', bad: false };
  if (b.error) return { text: `stopping renewals failed: ${b.error}`, bad: true };
  const failed: any[] = b.gateway_failed || [];
  if (failed.length) {
    const ids = failed.map(f => (typeof f === 'string' ? f : f?.subscription_id)).filter(Boolean).join(', ');
    return { text: `Razorpay did not confirm the stop for ${ids || 'a mandate'} — cancel manually in Razorpay`, bad: true };
  }
  const n = (b.gateway_cancelled || []).length;
  return { text: n ? `renewals stopped (${n} at Razorpay)` : 'no renewals to stop', bad: false };
};

/** The sentence after a suspension: what it actually did, from its log row. */
export const describeSuspension = (status: any): Exclude<Flash, null> => {
  const h = (status?.history || []).find((r: any) => r.action === 'disable') || {};
  const bill = billingText(h.billing);
  const parts = [
    `${Number(h.revoked_sessions || 0)} session${Number(h.revoked_sessions) === 1 ? '' : 's'} signed out`,
    bill.text,
    h.notified ? 'suspension notice e-mailed' : h.notify_error ? `notice NOT sent (${h.notify_error})` : 'no e-mail sent',
  ];
  if (Number(h.deletion_held) > 0) parts.push(`${h.deletion_held} deletion request(s) put on hold`);
  return { kind: bill.bad || h.notify_error ? 'error' : 'ok', text: `Suspended. ${parts.join(' · ')}.` };
};

/** A checkbox with a line under it saying what ticking it means. */
const Tick: React.FC<{
  checked: boolean; onChange: (v: boolean) => void; label: React.ReactNode; hint?: React.ReactNode;
}> = ({ checked, onChange, label, hint }) => (
  <label className="flex items-start gap-2 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
      className="mt-1 accent-primary-500" />
    <span className="flex flex-col gap-0.5 min-w-0">
      <span className="text-sm text-grayscale-1300">{label}</span>
      {hint && <span className="text-xs text-grayscale-1100">{hint}</span>}
    </span>
  </label>
);

const textareaCls = 'px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 w-full';

/** The suspend dialog. Also opened from a contact-abuse flag (Contact limits
 *  tab), so it fires the bell refresh itself and hands back the new status. */
export const SuspendAccountModal: React.FC<{
  userId: string; email?: string; onClose: () => void; onDone: (status: any) => void;
}> = ({ userId, email, onClose, onDone }) => {
  const [category, setCategory] = useState('contact_harvesting');
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(false);
  const [stopRenewals, setStopRenewals] = useState(true);
  const [holdDeletion, setHoldDeletion] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const why = !reason.trim() ? 'Give a reason first (kept internal)' : '';

  const submit = async () => {
    if (why || busy) return;
    setBusy(true);
    setErr('');
    const { data, error } = await panelPost('users', 'disable', {
      user_id: userId, category, reason: reason.trim().slice(0, 2000),
      notify, stop_renewals: stopRenewals, hold_deletion: holdDeletion,
    });
    setBusy(false);
    if (error || !data) { setErr(error || 'The suspension failed.'); return; }
    alertsChanged();
    onDone(data);
  };

  return (
    <Modal title="Suspend account" onClose={busy ? () => {} : onClose}
      footer={
        <>
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          {busy ? <Btn kind="danger" disabled>Suspending…</Btn>
            : why ? <Btn kind="danger" disabled title={why}>Suspend account</Btn>
              : <DangerBtn onConfirm={submit}>Suspend account</DangerBtn>}
        </>
      }>
      <div className="flex flex-col gap-3 max-h-[65vh] overflow-y-auto pr-1">
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 flex flex-col gap-1">
          <b className="text-sm">
            This locks {email ? <span className="break-all">{email}</span> : 'this account'} out, straight away.
          </b>
          <ul className="list-disc ml-4 flex flex-col gap-0.5">
            <li>Signs them out on every device.</li>
            <li>Blocks sign-in — e-mail code, password, Google and LinkedIn — and the WhatsApp bot.</li>
            <li>Blocks search, university contacts and everything else that needs their account.</li>
            <li>Their data stays. You can reinstate them later; sessions and stopped renewals do not come back by themselves.</li>
          </ul>
        </div>

        <Labeled label="What happened">
          <select value={category} onChange={e => setCategory(e.target.value)} className={inputCls}>
            {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
          </select>
        </Labeled>
        <Labeled label="Reason" hint={`required · ${reason.length}/2000 · kept internal — the user never sees it`}>
          <textarea value={reason} rows={3} maxLength={2000} autoFocus
            placeholder="e.g. Opened 96 university contact cards in one hour"
            onChange={e => setReason(e.target.value)} className={textareaCls} />
        </Labeled>

        <div className="flex flex-col gap-2">
          <Tick checked={notify} onChange={setNotify} label="E-mail the user a suspension notice"
            hint="A short notice that the account is suspended and how to reach support. It never includes the reason." />
          <Tick checked={stopRenewals} onChange={setStopRenewals} label="Stop renewals at Razorpay"
            hint="Nothing more is charged. A paid period keeps its end date, so if you reinstate them they still have the time they paid for." />
          <Tick checked={holdDeletion} onChange={setHoldDeletion} label="Hold any deletion request"
            hint="If they asked for their account to be deleted, the request waits instead of running, so the records stay." />
        </div>
        {err && <Msg kind="error">{err}</Msg>}
      </div>
    </Modal>
  );
};

/** Reinstating: optional reason. Nothing else is undone automatically. */
const ReinstateModal: React.FC<{
  userId: string; email?: string; onClose: () => void; onDone: (status: any) => void;
}> = ({ userId, email, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setBusy(true);
    setErr('');
    const { data, error } = await panelPost('users', 'enable',
      { user_id: userId, reason: reason.trim().slice(0, 2000) });
    setBusy(false);
    if (error || !data) { setErr(error || 'Reinstating failed.'); return; }
    alertsChanged();
    onDone(data);
  };

  return (
    <Modal title="Reinstate account" onClose={busy ? () => {} : onClose}
      footer={
        <>
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn kind="primary" onClick={submit} disabled={busy}>{busy ? 'Reinstating…' : 'Reinstate account'}</Btn>
        </>
      }>
      <div className="flex flex-col gap-3">
        <div className="text-sm text-grayscale-1300">
          {email ? <b className="break-all">{email}</b> : 'They'} can sign in and use the site again straight away.
        </div>
        <ul className="list-disc ml-4 flex flex-col gap-0.5 text-xs text-grayscale-1100">
          <li>They were signed out when suspended — they sign in again themselves.</li>
          <li>A Razorpay renewal that was stopped stays stopped; they would subscribe again.</li>
          <li>A deletion request put on hold stays on hold (Users → Deletion requests).</li>
        </ul>
        <Labeled label="Reason" hint={`optional · ${reason.length}/2000 · kept in the log`}>
          <textarea value={reason} rows={2} maxLength={2000} onChange={e => setReason(e.target.value)}
            className={textareaCls} />
        </Labeled>
        {err && <Msg kind="error">{err}</Msg>}
      </div>
    </Modal>
  );
};

/** "Account access" in UserDrawer: active or suspended, the Suspend /
 *  Reinstate controls, and every suspension and reinstatement so far. */
export const AccountAccessSection: React.FC<{ userId: string; email?: string; onChanged?: () => void }> =
  ({ userId, email, onChanged }) => {
  const st = useAdminGet('users', 'account_status', { user_id: userId }, [userId]);
  const [suspending, setSuspending] = useState(false);
  const [reinstating, setReinstating] = useState(false);
  const [msg, setMsg] = useState<Flash>(null);
  // A deep link can swap the account under an open drawer.
  useEffect(() => { setSuspending(false); setReinstating(false); setMsg(null); }, [userId]);

  const d = st.data;
  const who = email || d?.email || undefined;
  const history: Record<string, any>[] = d?.history || [];

  const suspended = (s: any) => {
    setSuspending(false);
    setMsg(describeSuspension(s));
    st.reload();
    onChanged?.();
  };
  const reinstated = () => {
    setReinstating(false);
    setMsg({ kind: 'ok', text: 'Reinstated — they can sign in again.' });
    st.reload();
    onChanged?.();
  };

  return (
    <Section title="Account access" right={<Btn small onClick={st.reload}>Refresh</Btn>}>
      {st.error && <Msg kind="error">{st.error}</Msg>}
      {st.loading && !d ? <Loading /> : !d ? null : d.suspended ? (
        <div className="flex flex-col gap-2">
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex flex-col gap-1">
            <div className="break-words">
              <b>Suspended</b> since {fmtDate(d.since)} by {d.by || 'unknown'} — {catLabel(d.category)}: {d.reason || 'no reason recorded'}
            </div>
            <div className="text-xs">
              They cannot sign in, use the WhatsApp bot, search or open university contacts. Only the team sees the reason.
            </div>
          </div>
          <div><Btn small kind="primary" onClick={() => { setMsg(null); setReinstating(true); }}>Reinstate…</Btn></div>
        </div>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-sm text-green-700">
            <span className="w-2 h-2 rounded-full bg-green-600" aria-hidden />
            <b>Active</b> — can sign in and use the site.
          </span>
          {d.can_suspend ? (
            <Btn small kind="danger" onClick={() => { setMsg(null); setSuspending(true); }}
              title="Lock this account out (for abuse) — asks for a reason first">
              Suspend account…
            </Btn>
          ) : (
            <span className="text-xs text-grayscale-1100">
              Team (admin) account — cannot be suspended. Remove them as an admin first (Billing → Admins).
            </span>
          )}
        </div>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      {history.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="text-xs font-bold text-grayscale-1200 font-grotesk">History</div>
          <div className="flex flex-col divide-y divide-grayscale-200 max-h-56 overflow-y-auto">
            {history.map((h, i) => {
              const off = h.action === 'disable';
              const bill = off ? billingText(h.billing) : null;
              return (
                <div key={h.id ?? i} className="py-1.5 flex flex-col gap-0.5 text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge tone={off ? 'red' : 'green'}>{off ? 'Suspended' : 'Reinstated'}</Badge>
                    <span className="text-grayscale-1100">
                      {fmtDate(h.created_at)} · by {h.by_email || 'unknown'}
                      {off && h.category ? ` · ${catLabel(h.category)}` : ''}
                    </span>
                  </div>
                  {h.reason && <div className="text-grayscale-1200 break-words">{h.reason}</div>}
                  {off && (
                    <div className="text-grayscale-1100">
                      {Number(h.revoked_sessions || 0)} session{Number(h.revoked_sessions) === 1 ? '' : 's'} signed out
                      {' · '}<span className={bill?.bad ? 'text-red-700' : ''}>{bill?.text}</span>
                      {' · '}{h.notified ? 'notice e-mailed'
                        : h.notify_error ? <span className="text-amber-700">notice NOT sent ({h.notify_error})</span>
                          : 'not e-mailed'}
                      {Number(h.deletion_held) > 0 ? ` · ${h.deletion_held} deletion request(s) held` : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {suspending && (
        <SuspendAccountModal userId={userId} email={who}
          onClose={() => setSuspending(false)} onDone={suspended} />
      )}
      {reinstating && (
        <ReinstateModal userId={userId} email={who}
          onClose={() => setReinstating(false)} onDone={reinstated} />
      )}
    </Section>
  );
};

export default AccountAccessSection;
